import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CForm,
  CFormInput,
  CFormSelect,
  CRow,
  CSpinner,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from "@coreui/react";

import ToastNotification from "components/globals/ToastNotification";
import { apiClient } from "services/Api";

const WEIGHT_FIELDS = [
  { key: "price", label: "Preço" },
  { key: "answer_capacity", label: "Cap. Resposta" },
  { key: "deadlines", label: "Prazos" },
  { key: "technical_capacity", label: "Cap. Técnica" },
  { key: "esg", label: "ESG" },
];

const DEFAULT_WEIGHT_CONFIGS = {
  2020: {
    price: 25,
    answer_capacity: 25,
    deadlines: 25,
    technical_capacity: 25,
    esg: 0,
  },
  2025: {
    price: 20,
    answer_capacity: 30,
    deadlines: 20,
    technical_capacity: 20,
    esg: 10,
  },
};

const getDefaultConfigForYear = (year) =>
  year >= 2025 ? DEFAULT_WEIGHT_CONFIGS[2025] : DEFAULT_WEIGHT_CONFIGS[2020];

const getClosestConfig = (configs, year) => {
  const candidates = (configs || [])
    .filter((row) => Number(row?.year) <= Number(year))
    .sort((a, b) => Number(b.year) - Number(a.year));

  if (candidates.length) {
    return candidates[0];
  }

  return null;
};

const buildDraftRow = (year, source = null, keepId = false) => {
  const fallback = getDefaultConfigForYear(year);

  return {
    id: keepId ? source?.id ?? null : null,
    year,
    ...Object.fromEntries(
      WEIGHT_FIELDS.map(({ key }) => [
        key,
        String(source?.[key] ?? fallback[key] ?? 0),
      ])
    ),
  };
};

const calcRowSum = (row) =>
  WEIGHT_FIELDS.reduce((total, { key }) => {
    const value = row?.[key];
    const numeric = value === "" || value === null || value === undefined ? 0 : Number(value);
    return total + (Number.isNaN(numeric) ? 0 : numeric);
  }, 0);

const SupplierEvaluationWeightsPage = () => {
  const currentYear = new Date().getFullYear();

  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [allWeights, setAllWeights] = useState([]);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [availableYears, setAvailableYears] = useState([currentYear]);
  const [row, setRow] = useState(buildDraftRow(currentYear));

  const fetchWeights = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.get("/supplier-evaluation-weights/");
      const data = response.data || [];
      setAllWeights(data);

      const years = Array.from(
        new Set([currentYear, selectedYear, ...data.map((item) => item.year).filter(Boolean)])
      ).sort((a, b) => b - a);
      setAvailableYears(years);
    } catch (error) {
      console.error("Error fetching supplier evaluation weights:", error);
      setToast({
        title: "Ponderações Avaliação de Fornecedores",
        message: "Erro ao carregar as ponderações.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [currentYear, selectedYear]);

  useEffect(() => {
    fetchWeights();
  }, [fetchWeights]);

  useEffect(() => {
    const exactConfig = allWeights.find((item) => Number(item.year) === Number(selectedYear));
    const sourceConfig = exactConfig || getClosestConfig(allWeights, selectedYear);

    setRow(buildDraftRow(selectedYear, sourceConfig, Boolean(exactConfig)));
  }, [allWeights, selectedYear]);

  const handleGenerate = async () => {
    setSaving(true);
    try {
      const response = await apiClient.post("/supplier-evaluation-weights/generate/", {
        year: selectedYear,
      });
      await fetchWeights();

      const sourceYear = response?.data?.source_year;
      const message =
        response?.data?.status === "already_exists"
          ? "Já existe um registo para este ano."
          : `Registo do ano gerado com sucesso${
              sourceYear ? ` a partir de ${sourceYear}` : ""
            }.`;

      setToast({
        title: "Ponderações Avaliação de Fornecedores",
        message,
        type: "success",
      });
    } catch (error) {
      console.error("Error generating supplier evaluation weights:", error);
      setToast({
        title: "Ponderações Avaliação de Fornecedores",
        message: "Erro ao gerar o registo do ano.",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleChangeField = (field) => (event) => {
    const value = event.target.value;
    if (value !== "" && Number.isNaN(Number(value))) {
      return;
    }

    setRow((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    if (calcRowSum(row) !== 100) {
      setToast({
        title: "Ponderações Avaliação de Fornecedores",
        message: "A soma das ponderações tem de ser exatamente 100%.",
        type: "error",
      });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        year: Number(selectedYear),
        ...Object.fromEntries(
          WEIGHT_FIELDS.map(({ key }) => [key, Number(row[key]) || 0])
        ),
      };

      const response = row.id
        ? await apiClient.put(`/supplier-evaluation-weights/${row.id}/`, payload)
        : await apiClient.post("/supplier-evaluation-weights/", payload);

      const recalculated = response?.data?.recalculated_evaluations || 0;

      setToast({
        title: "Ponderações Avaliação de Fornecedores",
        message: `Ponderações guardadas com sucesso. Foram recalculadas ${recalculated} avaliações.`,
        type: "success",
      });
      await fetchWeights();
    } catch (error) {
      console.error("Error saving supplier evaluation weights:", error);
      setToast({
        title: "Ponderações Avaliação de Fornecedores",
        message: "Erro ao guardar as ponderações.",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const total = useMemo(() => calcRowSum(row), [row]);

  return (
    <>
      <ToastNotification toast={toast} setToast={setToast} />

      <div className="d-flex justify-content-end mb-3">
        <CButton
          color="info"
          className="text-white"
          disabled={loading || saving}
          onClick={handleGenerate}
        >
          Gerar novas ponderações
        </CButton>
      </div>

      <CCard className="mb-4">
        <CCardHeader className="d-flex justify-content-between align-items-center">
          <div>
            <h3 className="mb-0">Configuração de Ponderações - Avaliação de Fornecedores</h3>
            <small className="text-muted">
              A avaliação usa o registo do ano igual ou anterior mais próximo. Ao guardar, as
              avaliações afetadas são recalculadas automaticamente.
            </small>
          </div>
          <CRow className="g-2 align-items-center">
            <CCol xs="auto">
              <strong>Ano:&nbsp;</strong>
            </CCol>
            <CCol xs="auto">
              <CFormSelect
                size="sm"
                value={selectedYear}
                onChange={(event) => setSelectedYear(parseInt(event.target.value, 10))}
              >
                {availableYears.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </CFormSelect>
            </CCol>
          </CRow>
        </CCardHeader>
        <CCardBody>
          {loading ? (
            <div className="text-center my-3">
              <CSpinner size="sm" /> A carregar...
            </div>
          ) : (
            <CForm
              onSubmit={(event) => {
                event.preventDefault();
                handleSave();
              }}
            >
              <div className="table-responsive">
                <CTable bordered small align="middle">
                  <CTableHead>
                    <CTableRow>
                      <CTableHeaderCell style={{ minWidth: 180 }}>Configuração</CTableHeaderCell>
                      {WEIGHT_FIELDS.map((field) => (
                        <CTableHeaderCell key={field.key} className="text-center">
                          {field.label}
                        </CTableHeaderCell>
                      ))}
                    </CTableRow>
                  </CTableHead>
                  <CTableBody>
                    <CTableRow key={selectedYear}>
                      <CTableDataCell className="fw-semibold">
                        Ano {selectedYear}
                        <div className="small text-muted">Total: {total}%</div>
                      </CTableDataCell>
                      {WEIGHT_FIELDS.map((field) => (
                        <CTableDataCell key={field.key} style={{ minWidth: 140 }}>
                          <CFormInput
                            type="number"
                            min="0"
                            max="100"
                            step="1"
                            value={row[field.key]}
                            onChange={handleChangeField(field.key)}
                          />
                        </CTableDataCell>
                      ))}
                    </CTableRow>
                  </CTableBody>
                </CTable>
              </div>

              <div className="d-flex justify-content-end mt-3">
                <CButton color="success" type="submit" disabled={saving}>
                  {saving ? <CSpinner size="sm" /> : "Guardar Ponderações do Ano"}
                </CButton>
              </div>
            </CForm>
          )}
        </CCardBody>
      </CCard>
    </>
  );
};

export default SupplierEvaluationWeightsPage;
